import type { TableHeadCellProps } from 'src/components/table';
import type { IContact } from 'src/pages/contacts/contacts.types';
import type { IAccount } from 'src/pages/accounts/accounts.types';
import type { ITeamItem, IUserItem } from 'src/pages/setting/setting.types';

import { toast } from 'sonner';
import { useState, useEffect, useCallback } from 'react';

import {
  Dialog,
  useTheme,
  IconButton,
  DialogTitle,
  DialogContent,
  type DialogProps,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { getAssigneName } from 'src/utils/commonUtils';

import { useTranslate } from 'src/locales';
import { useFilterChannelMutation } from 'src/pages/channel/slice/channelApi';
import { useFilterAccountMutation } from 'src/pages/accounts/slice/accountsApi';
import { useFilterContactMutation } from 'src/pages/contacts/slice/contactsApi';
import {
  useFilterUserDetailMutation,
  useGetFilterTeamListMutation,
} from 'src/pages/setting/slice/settingApi';

import { Iconify } from 'src/components/iconify';
import { TableCustom } from 'src/components/table/table-custom';

import { type ChannelListType, useGetMediaUrlMutation } from '../slice/chatApi';

type Props = DialogProps & {
  open: boolean;
  onClose: () => void;
  handleSubmitData?: (newTeam: { assignUsers: string[]; teamName: string; status: string }) => void;
  type: '' | 'Contact' | 'Channel' | 'Account' | 'User' | 'Team';
  setRecordInputHandler?: (row: any) => void;
  isOnlyWaChannel?: boolean;
  isAccountView?: boolean;
  currentContactId?: string | null;
};

type QueryParams = {
  orderBy: string;
  page: number;
  rowsPerPage: number;
  globalSearch: string;
  fieldSearch: Record<string, string>;
  order: string;
};

interface IDefaultFilter {
  filters: [] | { fieldname: string; search?: string; sort?: number }[];
  globalSearch: string;
  page: number;
  limit: number;
}

type renderTableType = {
  t: (key: string) => string;
  tableData: any[];
  type: '' | 'Contact' | 'Channel' | 'Account' | 'User' | 'Team';
  handleQueryChange: (newParams: QueryParams) => void;
  totalPageCount: number;
  setRecordInputHandler?: (row: any) => void;
  isLoader: boolean;
};

type FilterObj = { fieldname: string; search?: string; sort?: number };

export function SelectRecordDialog({
  open,
  onClose,
  handleSubmitData,
  type,
  setRecordInputHandler,
  currentContactId = null,
  isAccountView = false,
  isOnlyWaChannel = false,
  ...other
}: Props) {
  const defaultFilter = {
    filters: [{ fieldname: 'createdAt', sort: 1 }],
    globalSearch: '',
    page: 1,
    limit: 10,
  };

  const theme = useTheme();

  const { t } = useTranslate('translation');

  const [tableData, setTableData] = useState<
    ChannelListType[] | IContact[] | IAccount[] | ITeamItem[] | IUserItem[]
  >([]);
  const [queryParams, setQueryParams] = useState<IDefaultFilter>(defaultFilter);
  const [totalPageCount, setTotalPageCount] = useState(0);

  const [filterContact, { isLoading: isContactLoading }] = useFilterContactMutation();
  const [filterChannel, { isLoading: isChannelLoading }] = useFilterChannelMutation();
  const [filterAccount, { isLoading: isAccountLoading }] = useFilterAccountMutation();
  const [getFilterTeamList, { isLoading: isTeamLoading }] = useGetFilterTeamListMutation();
  const [filterUserList, { isLoading: isUserLoading }] = useFilterUserDetailMutation();
  const [getMediaUrl, { isLoading: isgetMediaLoading }] = useGetMediaUrlMutation();

  // Combine all loading states
  const isLoader =
    isContactLoading ||
    isChannelLoading ||
    isAccountLoading ||
    isTeamLoading ||
    isUserLoading ||
    isgetMediaLoading;

  const fldListObj = {
    Contact: ['firstName', 'lastName', 'phone', 'email', 'country'],
    Channel: ['channelName'],
    Account: ['name', 'email', 'phone', 'crNumber', 'city', 'country'],
    User: ['firstName', 'lastName', 'email'],
    Team: ['teamName', 'createdDate', 'status'],
  };

  const orgFldNames = {
    Team: { teamName: 'name', createdDate: 'createdAt', status: 'status' },
  };

  const fields = fldListObj[type as keyof typeof fldListObj];

  if (!fields) throw new Error(`Filter Fields Not found => ${type}`);

  const handleQueryChange = useCallback((newParams: QueryParams) => {
    const transformedData = {
      filters: fields.reduce(
        (acc, field) => {
          const updatedFields =
            type === 'Team'
              ? orgFldNames[type as 'Team'][field as keyof (typeof orgFldNames)['Team']]
              : field;

          const filterObj: FilterObj = { fieldname: updatedFields };

          const hasSearch =
            newParams.fieldSearch[field] !== undefined && newParams.fieldSearch[field] !== '';
          const hasSort = field === newParams.orderBy;

          if (hasSearch) {
            filterObj.search = newParams.fieldSearch[field];
          }

          if (hasSort) {
            filterObj.sort = newParams.order === 'asc' ? 0 : 1;
          }

          if (hasSearch || hasSort) {
            acc.push(filterObj);
          }

          return acc;
        },
        [] as { fieldname: string; search?: string; sort?: number }[]
      ),

      globalSearch: newParams.globalSearch || '',
      page: newParams.page + 1,
      limit: newParams.rowsPerPage,
    };

    transformedData.filters.push({ fieldname: 'createdAt', sort: 1 });

    setQueryParams((prevState) => {
      const prevData = JSON.stringify(prevState);
      const newData = JSON.stringify(transformedData);

      return prevData !== newData ? transformedData : prevState;
    });
  }, []);

  const getProfileUrl = async (filePath: string | null) => {
    if (filePath) {
      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath });
      return uploadFileUrl;
    }
    return '';
  };

  const getChannelListWithConfig = (chlList: any) => {
    if (!chlList || chlList.length === 0) return [];

    return chlList.map((list: any) => {
      const assignUserListName = getAssigneName(
        list.assignedType === 'USER' ? list.assignedUser : list.assignedTeam,
        list.assignedType
      );

      let channelConfig = '';
      switch (list.channelType) {
        case 'VOICE':
          channelConfig = list?.VoiceConfig?.[0]?.didNum ?? '';
          break;
        case 'WHATSAPP':
          channelConfig = list?.WhatsAppConfig?.[0]?.phoneNumber ?? '';
          break;
        case 'EMAIL':
          channelConfig = list?.EmailConfig?.[0]?.email ?? '';
          break;
        case 'LIVE_CHAT':
          channelConfig = list?.LiveChatConfig?.[0]?.domainUrl ?? '';
          break;
        default:
          channelConfig = '';
          break;
      }

      return {
        ...list,
        channelConfig,
        assignUserListName,
      };
    });
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiFuncObj = {
          Account: filterAccount,
          Channel: filterChannel,
          Contact: filterContact,
          User: filterUserList,
          Team: getFilterTeamList,
        };

        const apiFunc = apiFuncObj[type as keyof typeof apiFuncObj];
        if (!apiFunc) throw new Error(`Filter API function not found for type: ${type}`);

        apiFuncObj[type as keyof typeof apiFuncObj](queryParams).then(async (res: any) => {
          setTotalPageCount(res?.data?.total || 0);
          if (isOnlyWaChannel) {
            const filteredList =
              res?.data?.list.filter((l: any) => l.channelType === 'WHATSAPP') || [];
            setTableData(filteredList);
          } else if (type === 'Channel') {
            setTableData(getChannelListWithConfig(res.data?.list));
          } else {
            setTableData(
              (await Promise.all(
                res.data?.list.map(async (data: any) => ({
                  ...data,
                  ProfileURL: data.ProfileURL ? await getProfileUrl(data.ProfileURL) : '',
                }))
              )) || []
            );
          }
        });
      } catch (err) {
        console.error('Error filtering records:', { error: err, type });
        toast.error(t('RECORD_FETCHING_FAILED'));
      }
    };

    fetchData();
  }, [queryParams]); // ✅ Only runs when queryParams changes

  useEffect(() => {
    if (isAccountView && currentContactId) {
      setQueryParams((prev: any) => ({
        ...prev,
        filters: [
          ...(prev && prev.filters ? prev.filters : []),
          { fieldname: 'companyId', search: currentContactId },
        ],
        page: 1,
      }));
    }
  }, [isAccountView, currentContactId]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      // maxWidth={type === 'Channel' ? 'sm' : 'md'}
      open={open}
      onClose={onClose}
      {...other}
    >
      <DialogTitle
        sx={[
          (newtheme) => ({
            p: newtheme.spacing(3, 3, 2, 3),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }),
        ]}
      >
        <span>{t(`SELECT_${type.toUpperCase()}`)}</span>
        <IconButton aria-label="search" onClick={onClose}>
          <Iconify
            icon="eva:close-fill"
            width={24}
            sx={{
              color: theme.vars.palette.common.black,
              ...theme.applyStyles('dark', {
                color: theme.vars.palette.common.white,
              }),
            }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1, pb: 1, border: 'none' }}>
        <RenderTable
          t={t}
          tableData={tableData}
          type={type}
          handleQueryChange={handleQueryChange}
          totalPageCount={totalPageCount}
          setRecordInputHandler={setRecordInputHandler}
          isLoader={isLoader}
        />
      </DialogContent>
    </Dialog>
  );
}

const RenderTable = ({
  t,
  tableData,
  type,
  handleQueryChange,
  totalPageCount,
  setRecordInputHandler,
  isLoader = false,
}: renderTableType) => {
  const CONTACT_TABLE_HEAD: TableHeadCellProps[] = [
    { id: 'firstName', label: t('FIRST_NAME'), width: 200, filterable: true },
    { id: 'lastName', label: t('LAST_NAME'), width: 200, filterable: true },
    { id: 'phone', label: t('PHONE_NUM'), width: 200 },
    { id: 'email', label: t('EMAIL'), width: 200 },
  ];

  const CHANNEL_TABLE_HEAD: TableHeadCellProps[] = [
    { id: 'channelName', label: t('CHANNEL_NAME'), width: 600 },
    { id: 'channelConfig', label: t('CHANNEL_CONFIG'), width: 300 },
  ];

  const ACCOUNT_TABLE_HEAD: TableHeadCellProps[] = [
    { id: 'name', label: t('ACCOUNT_NAME'), width: 200, filterable: true },
    // { id: 'website', label: t('WEBSITE'), width: 200 },
    { id: 'email', label: t('EMAIL'), width: 200 },
    { id: 'phone', label: t('CONTACT_NUM'), width: 200 },
    // { id: 'address', label: t('ADDRESS'), width: 200 },
    { id: 'crNumber', label: t('CR_NUMBER'), width: 100 },
    // { id: 'pincode', label: t('PINCODE'), width: 100 },
    { id: 'city', label: t('CITY'), width: 100 },
    { id: 'country', label: t('COUNTRY'), width: 100 },
  ];

  const USER_TABLE_HEAD: TableHeadCellProps[] = [
    { id: 'firstName', label: t('FIRST_NAME'), width: 200 },
    { id: 'lastName', label: t('LAST_NAME'), width: 200 },
    { id: 'email', label: t('EMAIL'), width: 180 },
    { id: 'role', label: t('ROLE'), width: 100 },
    { id: 'joinedDate', label: t('JOINED_DATE'), width: 100 },
    { id: 'status', label: t('STATUS'), width: 100 },
  ];

  const TEAM_TABLE_HEAD: TableHeadCellProps[] = [
    { id: 'teamName', label: t('TEAM_NAME'), width: 300 },
    { id: 'noOfUsers', label: t('NO_OF_USERS'), width: 100 },
    { id: 'createdDate', label: t('CREATED_DATE'), width: 100 },
    { id: 'status', label: t('STATUS'), width: 100 },
  ];

  const getColumns = () => {
    switch (type) {
      case 'Contact':
        return CONTACT_TABLE_HEAD;
      case 'Channel':
        return CHANNEL_TABLE_HEAD;
      case 'Account':
        return ACCOUNT_TABLE_HEAD;
      case 'User':
        return USER_TABLE_HEAD;
      case 'Team':
        return TEAM_TABLE_HEAD;
      default:
        return [];
    }
  };

  const editHref = (id: string): string => paths.contact.details(id);

  const typeBaseSortFldList: Record<
    '' | 'Contact' | 'Channel' | 'Account' | 'User' | 'Team',
    string[]
  > = {
    Contact: [],
    Channel: [],
    Account: [],
    User: [],
    Team: ['teamName', 'createdDate', 'status'],
    '': [],
  };

  return (
    <TableCustom
      columns={getColumns()}
      data={tableData}
      tableToolbar
      editHref={editHref}
      // isToolbarVisible={false}
      isCheckboxVisible={false}
      rowHeight
      onQueryChange={handleQueryChange}
      totalRecordLength={totalPageCount}
      // sortFldList={['teamName', 'status']}
      // isActionButton={false}
      loader={isLoader}
      isShowCreateBtn={false}
      isSelectRowPremission={false}
      isSelectRecordDialog
      setRecordInputHandler={setRecordInputHandler}
      isSearchFullWidth
      sortFldList={typeBaseSortFldList[type] || []}
    />
  );
};

// const [getContactsList] = useGetContactsListMutation();
// const [getChannelList] = useGetChannelListMutation();
// const [getAccountsList] = useGetAccountsListMutation();
// const [getTeamList] = useGetTeamListMutation();
// const [getUserList] = useGetUserListMutation();

// useEffect(() => {
//   const apiFuncObj = {
//     Account: getAccountsList,
//     Channel: getChannelList,
//     Contact: getContactsList,
//     User: getUserList,
//     Team: getTeamList,
//   };

//   const apiFunc = apiFuncObj[type as keyof typeof apiFuncObj];
//   if (!apiFunc) throw new Error(`API function not found for type: ${type}`);

//   apiFuncObj[type as keyof typeof apiFuncObj](defaultFilter)
//     .then(
//       (res: {
//         data?: {
//           list: IContact[] | ChannelListType[] | IAccount[] | ITeamItem[] | IUserItem[];
//           total: number;
//         };
//       }) => {
//         setTotalPageCount(res?.data?.total || 0);
//         setTableData(res?.data?.list || []);
//       }
//     )
//     .catch((err: any) => {
//       console.error('Error in fetchData:', { error: err, type });
//       toast.error('Failed to fetch records. Please try again later.');
//     });

//   // eslint-disable-next-line react-hooks/exhaustive-deps
// }, [type]);
